import { useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackCallEvent } from "@/lib/analytics";
import logo from "@/assets/logo.webp";

const serviceSubLinks = [
  { href: "/facials", label: "Aesthetic Facials" },
  { href: "/chemical-peels", label: "Chemical Peels" },
  { href: "/massage-therapy", label: "Massage Therapy" },
];

const productSubLinks = [
  { href: "/products#enzyme-sets", label: "Enzyme Sets" },
  { href: "/products#masks-treatments", label: "Masks & Treatments" },
  { href: "/products#cleansers", label: "Cleansers" },
  { href: "/products#treatment-serums", label: "Treatment Serums" },
  { href: "/products#hydration", label: "Hydration" },
  { href: "/products#eyes", label: "Eyes" },
  { href: "/products#after-peel", label: "After Peel" },
];

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/services", label: "Medi-Facials", children: serviceSubLinks },
  { href: "/products", label: "Products", children: productSubLinks },
  { href: "/blog", label: "Blog" },
  { href: "/gallery", label: "Gallery" },
  { href: "/service-areas", label: "Service Areas" },
  { href: "/faq", label: "FAQ" },
  { href: "/contact", label: "Contact" },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isActivePage = (href: string, children?: { href: string }[]) => {
    if (location.pathname === href) return true;
    return children?.some((c) => location.pathname === c.href || location.pathname + location.hash === c.href) ?? false;
  };

  const handleHashLink = useCallback((e: React.MouseEvent, href: string) => {
    const [path, hash] = href.split("#");
    if (hash && location.pathname === path) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else if (hash) {
      // Navigate to page, then scroll after load
      navigate(href);
    }
    setOpenDropdown(null);
    setIsMenuOpen(false);
  }, [location.pathname, navigate]);

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
      <div className="container flex items-center justify-between h-20 md:h-24">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src={logo}
            alt="Ivana Glow Medical Spa - Medical Aesthetics in Lake Forest IL"
            width={256}
            height={256}
            fetchPriority="high"
            decoding="async"
            className="h-28 md:h-32 w-auto -my-4"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.children ? (
              <div
                key={link.href}
                className="relative group"
                onMouseEnter={() => setOpenDropdown(link.href)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  to={link.href}
                  className={`px-4 py-2 rounded-md font-medium transition-colors hover:text-primary inline-flex items-center gap-1 ${
                    isActivePage(link.href, link.children) ? "text-primary bg-primary/5" : "text-foreground"
                  }`}
                >
                  {link.label}
                  <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                </Link>
                {openDropdown === link.href && (
                  <div className="absolute top-full left-0 pt-1 z-50">
                    <div className="bg-background border border-border rounded-lg shadow-lg py-2 min-w-[200px]">
                      <Link
                        to={link.href}
                        className="block px-4 py-2 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                      >
                        All {link.label}
                      </Link>
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={(e) => handleHashLink(e, child.href)}
                          className={`block px-4 py-2 text-sm transition-colors hover:bg-muted hover:text-primary ${
                            (location.pathname + location.hash) === child.href || location.pathname === child.href ? "text-primary bg-primary/5" : "text-foreground"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-md font-medium transition-colors hover:text-primary ${
                  location.pathname === link.href ? "text-primary bg-primary/5" : "text-foreground"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* CTA Button */}
        <div className="flex items-center gap-2">
          <Button asChild variant="cta" size="lg" className="hidden sm:flex">
            <a href="tel:+18472830204" onClick={trackCallEvent}>
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </Button>

          <a
            href="tel:+18472830204"
            onClick={trackCallEvent}
            className="sm:hidden p-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            aria-label="Call us"
          >
            <Phone className="w-5 h-5" />
          </a>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-md hover:bg-muted transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background animate-fade-in-up max-h-[calc(100vh-5rem)] overflow-y-auto">
          <nav className="container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <div key={link.href}>
                {link.children ? (
                  <button
                    onClick={() => setMobileDropdown(mobileDropdown === link.href ? null : link.href)}
                    className={`w-full px-4 py-3 rounded-md font-medium transition-colors flex items-center justify-between ${
                      isActivePage(link.href, link.children) ? "text-primary bg-primary/5" : "text-foreground hover:bg-muted"
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileDropdown === link.href ? "rotate-180" : ""}`} />
                  </button>
                ) : (
                  <Link
                    to={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-3 rounded-md font-medium transition-colors flex items-center justify-between ${
                      location.pathname === link.href ? "text-primary bg-primary/5" : "text-foreground hover:bg-muted"
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
                {link.children && mobileDropdown === link.href && (
                  <div className="ml-4 flex flex-col gap-1">
                    <Link
                      to={link.href}
                      onClick={() => setIsMenuOpen(false)}
                      className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        location.pathname === link.href && !location.hash ? "text-primary bg-primary/5" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      All {link.label}
                    </Link>
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={(e) => { handleHashLink(e, child.href); setIsMenuOpen(false); }}
                        className={`px-4 py-2 rounded-md text-sm transition-colors ${
                          (location.pathname + location.hash) === child.href || location.pathname === child.href ? "text-primary bg-primary/5" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Button asChild variant="cta" size="lg" className="mt-4">
              <a href="tel:+18472830204" onClick={trackCallEvent}>
                <Phone className="w-4 h-4" />
                Call Now: (847) 283-0204
              </a>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
